import {
  EventStatusDetails,
  WorkflowStateEventTriggerBody,
} from '@/types/workflow-state-events'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import queryKeys from '../query-keys'
import { createDetail, getList, updateDetail } from '../request-wrappers'

/**
 * MARK: Action Hook: Get Workflow State Event Statuses
 * Gets the statuses for workflow state events currently in motion.
 */
export function useWorkflowStateEventStatuses(
  options: UseGetListOptions<EventStatusDetails>,
) {
  const query = useGetList<EventStatusDetails>({
    queryKey: queryKeys.events.list(
      {
        filters: options.filters,
      },
      ['event_statuses'],
    ),
    serviceFn: (serviceFnOptions) => {
      return getList<EventStatusDetails>({
        url: `/v2/pd-workflow/events`,
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    eventStatuses: query.allData,
    ...query,
  }
}

/**
 * MARK: Action Hook: Fail Event
 * Fails an on-going event.
 */
export function useFailEvent(
  options: UseUpdateDetailOptions<{ id: string }> = {},
) {
  const mutation = useUpdateDetail<{ id: string }>({
    serviceFn: ({ item, ...serviceFnOptions }) => {
      return updateDetail({
        url: `/v2/pd-workflow/events/${item.id}/fail`,
        item,
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    failEvent: mutation.mutate,
    failEventAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Trigger Workflow State Event
 * Triggers a workflow state event.
 */
export function useTriggerWorkflowStateEvent(
  options: UseCreateDetailOptions<
    WorkflowStateEventTriggerBody,
    EventStatusDetails
  > = {},
) {
  const mutation = useCreateDetail<
    WorkflowStateEventTriggerBody,
    EventStatusDetails
  >({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/pd-workflow/events',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    triggerWorkflowStateEvent: mutation.mutate,
    triggerWorkflowStateEventAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Cancel Events
 * Cancels all on-going events for a document.
 */
export function useCancelEvents(
  options: UseCreateDetailOptions<{ id: string }> = {},
) {
  const mutation = useCreateDetail<{ id: string }>({
    serviceFn: ({ item, filters, axiosOptions }) => {
      return createDetail({
        url: '/v2/pd-workflow/events/documents/cancel',
        item: {},
        filters: { ...filters, document_id: item.id },
        axiosOptions,
      })
    },
    ...options,
  })

  return {
    cancelEvents: mutation.mutate,
    cancelEventsAsync: mutation.mutateAsync,
    ...mutation,
  }
}
